import { useId, useState, type KeyboardEvent } from 'react'
import { Plus, Trash2, X } from 'lucide-react'
import { Select, type SelectOption } from '../../admin/primitives.js'
import { Checkbox, NumberInput, Switch, TextInput } from '../../admin/form.js'
import {
  JOURS_COURTS, JOURS_LONGS, JSON_DEDIES, finLendemain, minEffectif, modeDe, phraseFenetre, phraseSens, plagesParJour,
  type Brouillon, type Fenetre, type LigneDico, type OptionSonde, type ReglagePublie,
} from './modele.js'

/**
 * UN COMPOSANT PAR TYPE de réglage de Sonde (16 types). `ChampReglage`
 * choisit le bon à partir du schéma, sans connaître aucun réglage, sauf les
 * 5 réglages `json` qui ont un tableau dédié (modele.ts, JSON_DEDIES). Un
 * type inconnu de Gestion se règle en texte JSON validé.
 *
 * Primitives du design system seulement (Select premium, NumberInput à unité
 * intégrée, Switch, Checkbox, lc-input) ; aucun contrôle natif visible qui
 * casse le design, sauf les sélecteurs d'heure natifs (clavier et mobile).
 */

export interface ContexteChamps {
  /** Salles et zones de Sonde (GET zones), null tant qu'elles ne sont pas lues. */
  zones: OptionSonde[] | null
  /** Options restreintes pour un réglage (ex. lectures par POST propres à une source). */
  optionsPour?: (cle: string) => OptionSonde[] | null
}

interface PropsChamp {
  s: ReglagePublie
  b: Brouillon
  onChange: (b: Brouillon) => void
  desactive: boolean
  ctx: ContexteChamps
}

export function ChampReglage(p: PropsChamp) {
  const mode = modeDe(p.s, p.s.default)
  const b = p.b
  if (b.t === 'bool') return <ChampBool {...p} b={b} />
  if (b.t === 'seuil') return <ChampSeuil {...p} b={b} />
  if (b.t === 'fenetres') return <ChampFenetres {...p} b={b} />
  if (b.t === 'carte') return <ChampCarte {...p} b={b} />
  if (b.t === 'dico') return <ChampDico {...p} b={b} />
  if (b.t === 'json') return <ChampJson {...p} b={b} />
  if (b.t === 'liste') {
    const opts = p.ctx.optionsPour?.(p.s.key) ?? p.s.options
    if (p.s.type === 'string_list' && opts && opts.length > 0) return <ChampCases {...p} b={b} options={opts} />
    if (p.s.type === 'integer_list' && p.s.item_min === 1 && p.s.item_max === 7) return <ChampJours {...p} b={b} />
    return <ChampPuces {...p} b={b} />
  }
  // b.t === 'texte'
  if (mode === 'integer' || mode === 'number') return <ChampNombre {...p} b={b} />
  if (mode === 'enum') return <ChampEnum {...p} b={b} />
  if (mode === 'time_of_day') return <ChampHeure {...p} b={b} />
  if (mode === 'datetime') return <ChampDateHeure {...p} b={b} />
  return <ChampTexte {...p} b={b} />
}

type B<T extends Brouillon['t']> = Extract<Brouillon, { t: T }>

/* ─── boolean ─── */
function ChampBool({ s, b, onChange, desactive }: PropsChamp & { b: B<'bool'> }) {
  return (
    <span className="sv-reg-bool">
      <Switch checked={b.v} onChange={(v) => onChange({ t: 'bool', v })} disabled={desactive} ariaLabel={s.label} />
      <span className={b.v ? 'sv-reg-bool__oui' : 'sv-reg-bool__non'}>{b.v ? 'Activé' : 'Désactivé'}</span>
    </span>
  )
}

/* ─── integer, number ─── */
function ChampNombre({ s, b, onChange, desactive }: PropsChamp & { b: B<'texte'> }) {
  const min = minEffectif(s)
  const entier = s.type === 'integer'
  if (desactive) return <span className="sv-reg-lecture">{b.v === '' ? '—' : `${Number(b.v).toLocaleString('fr-FR')}${s.unit ? ` ${s.unit}` : ''}`}</span>
  return (
    <span className="sv-reg-nombre">
      <NumberInput
        value={b.v} onChange={(v) => onChange({ t: 'texte', v })} unit={s.unit}
        {...(min !== null ? { min } : {})} {...(s.max !== null ? { max: s.max } : {})}
        step={s.step ?? (entier ? 1 : 0.1)} steppers={entier} ariaLabel={s.label}
        placeholder={s.nullable ? 'vide' : '…'}
      />
      {s.nullable ? <span className="lc-hint">vide = aucune valeur</span> : null}
    </span>
  )
}

/* ─── enum ─── */
function ChampEnum({ s, b, onChange, desactive }: PropsChamp & { b: B<'texte'> }) {
  const opts: SelectOption[] = (s.options ?? []).map((o) => ({ value: o.value, label: o.label }))
  if (s.nullable) opts.unshift({ value: '', label: '— aucune valeur —' })
  if (desactive) return <span className="sv-reg-lecture">{opts.find((o) => o.value === b.v)?.label ?? (b.v || '—')}</span>
  return <Select value={b.v} options={opts} onChange={(v) => onChange({ t: 'texte', v })} ariaLabel={s.label} placeholder="—" />
}

/* ─── string, url ─── */
function ChampTexte({ s, b, onChange, desactive }: PropsChamp & { b: B<'texte'> }) {
  if (desactive) return <span className={`sv-reg-lecture${s.type === 'url' ? ' sv-mono' : ''}`}>{b.v || '—'}</span>
  return (
    <TextInput value={b.v} onChange={(v) => onChange({ t: 'texte', v })} ariaLabel={s.label} disabled={desactive} maxLength={500}
               placeholder={s.type === 'url' ? 'https://…' : s.nullable ? 'vide = aucune valeur' : ''} />
  )
}

/* ─── time_of_day ─── */
function ChampHeure({ s, b, onChange, desactive }: PropsChamp & { b: B<'texte'> }) {
  if (desactive) return <span className="sv-reg-lecture">{b.v || '—'} <span className="lc-hint">heure de Paris</span></span>
  return (
    <span className="sv-reg-heure">
      <input className="lc-input" type="time" step={60} value={b.v} disabled={desactive} aria-label={`${s.label} (heure de Paris)`}
             onChange={(e) => onChange({ t: 'texte', v: e.target.value })} />
      <span className="lc-hint">heure de Paris</span>
    </span>
  )
}

/* ─── datetime ─── */
function ChampDateHeure({ s, b, onChange, desactive }: PropsChamp & { b: B<'texte'> }) {
  return (
    <span className="sv-reg-heure">
      <input className="lc-input" type="datetime-local" step={60} value={b.v} disabled={desactive} aria-label={`${s.label} (heure de Paris)`}
             onChange={(e) => onChange({ t: 'texte', v: e.target.value })} />
      <span className="lc-hint">heure de Paris</span>
      {s.nullable && b.v && !desactive ? (
        <button type="button" className="lc-btn lc-btn--ghost" onClick={() => onChange({ t: 'texte', v: '' })}>Vider</button>
      ) : null}
    </span>
  )
}

/* ─── string_list avec options : cases à cocher (liste fermée) ─── */
function ChampCases({ s, b, onChange, desactive, options }: PropsChamp & { b: B<'liste'>; options: OptionSonde[] }) {
  const coche = new Set(b.v)
  // Une valeur du serveur absente des options reste visible (jamais perdue en silence).
  const toutes = [...options, ...b.v.filter((v) => !options.some((o) => o.value === v)).map((v) => ({ value: v, label: v }))]
  return (
    <div className="sv-reg-cases" role="group" aria-label={s.label}>
      {toutes.map((o) => (
        <Checkbox key={o.value} checked={coche.has(o.value)} disabled={desactive} label={o.label}
                  onChange={(on) => onChange({ t: 'liste', v: on ? [...b.v, o.value] : b.v.filter((x) => x !== o.value) })} />
      ))}
    </div>
  )
}

/* ─── integer_list 1..7 : les jours ISO ─── */
function ChampJours({ s, b, onChange, desactive }: PropsChamp & { b: B<'liste'> }) {
  return <Jours label={s.label} jours={b.v.map(Number)} desactive={desactive} onChange={(j) => onChange({ t: 'liste', v: j.map(String) })} />
}

function Jours({ label, jours, onChange, desactive }: { label: string; jours: number[]; onChange: (j: number[]) => void; desactive: boolean }) {
  const on = new Set(jours)
  return (
    <div className="sv-reg-jours" role="group" aria-label={label}>
      {JOURS_COURTS.map((j, i) => {
        const d = i + 1
        return (
          <button key={j} type="button" className={`sv-reg-jour${on.has(d) ? ' sv-reg-jour--on' : ''}`} aria-pressed={on.has(d)}
                  aria-label={JOURS_LONGS[i]} disabled={desactive}
                  onClick={() => onChange(on.has(d) ? jours.filter((x) => x !== d) : [...jours, d].sort((a, c) => a - c))}>
            {j}
          </button>
        )
      })}
    </div>
  )
}

/* ─── url_list, email_list, string_list, integer_list : éditeur de puces ─── */
function ChampPuces({ s, b, onChange, desactive }: PropsChamp & { b: B<'liste'> }) {
  const [saisie, setSaisie] = useState('')
  const plein = s.max_items !== null && b.v.length >= s.max_items
  const ajouter = () => {
    const morceaux = saisie.split(/[\n,;]+/).map((x) => x.trim()).filter(Boolean)
    if (morceaux.length === 0) return
    onChange({ t: 'liste', v: [...b.v, ...morceaux.filter((m) => !b.v.includes(m))] })
    setSaisie('')
  }
  const touche = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); ajouter() }
  }
  const ph = s.type === 'url_list' ? 'https://…' : s.type === 'email_list' ? 'adresse@exemple.fr'
    : s.type === 'integer_list' ? `nombre${s.item_min !== null ? ` de ${s.item_min}` : ''}${s.item_max !== null ? ` à ${s.item_max}` : ''}` : 'ajouter…'
  return (
    <div className="sv-reg-puces">
      {b.v.length === 0 ? <span className="lc-hint">Aucun élément.</span> : (
        <ul className="sv-reg-puces__liste" aria-label={s.label}>
          {b.v.map((x, i) => (
            <li key={`${x}-${i}`} className="sv-reg-puce">
              <span className={s.type === 'string_list' ? undefined : 'sv-mono'}>{x}</span>
              {!desactive ? (
                <button type="button" className="sv-reg-puce__x" aria-label={`Retirer ${x}`}
                        onClick={() => onChange({ t: 'liste', v: b.v.filter((_, j) => j !== i) })}>
                  <X size={14} aria-hidden="true" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
      {!desactive ? (
        <span className="sv-reg-puces__ajout">
          <input className="lc-input" value={saisie} placeholder={plein ? `${s.max_items} au plus` : ph} disabled={plein}
                 aria-label={`Ajouter à « ${s.label} »`} onChange={(e) => setSaisie(e.target.value)} onKeyDown={touche}
                 inputMode={s.type === 'integer_list' ? 'numeric' : s.type === 'email_list' ? 'email' : s.type === 'url_list' ? 'url' : 'text'} />
          <button type="button" className="lc-btn" onClick={ajouter} disabled={plein || !saisie.trim()}>
            <Plus size={14} aria-hidden="true" /> Ajouter
          </button>
        </span>
      ) : null}
      {s.max_items !== null ? <span className="lc-hint">{b.v.length}/{s.max_items}</span> : null}
    </div>
  )
}

/* ─── weekly_windows : créneaux + grille de la semaine ─── */
function ChampFenetres({ s, b, onChange, desactive }: PropsChamp & { b: B<'fenetres'> }) {
  const maj = (i: number, f: Partial<Fenetre>) => onChange({ t: 'fenetres', v: b.v.map((x, j) => j === i ? { ...x, ...f } : x) })
  const plein = s.max_items !== null && b.v.length >= s.max_items
  return (
    <div className="sv-reg-fenetres">
      <GrilleSemaine fenetres={b.v} />
      {b.v.length === 0 ? <p className="lc-hint">Aucun créneau.</p> : null}
      <ol className="sv-reg-creneaux">
        {b.v.map((f, i) => (
          <li key={i} className="sv-reg-creneau">
            <div className="sv-reg-creneau__tete">
              <span className="sv-reg-creneau__titre">Créneau {i + 1}</span>
              {!desactive ? (
                <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer le créneau ${i + 1}`}
                        onClick={() => onChange({ t: 'fenetres', v: b.v.filter((_, j) => j !== i) })}>
                  <Trash2 size={15} aria-hidden="true" />
                </button>
              ) : null}
            </div>
            <Jours label={`Jours du créneau ${i + 1}`} jours={f.days} desactive={desactive} onChange={(days) => maj(i, { days })} />
            <div className="sv-reg-creneau__heures">
              <label className="sv-reg-creneau__h">
                <span className="lc-label">Début</span>
                <input className="lc-input" type="time" step={60} value={f.start} disabled={desactive} onChange={(e) => maj(i, { start: e.target.value })} />
              </label>
              <label className="sv-reg-creneau__h">
                <span className="lc-label">Fin</span>
                <input className="lc-input" type="time" step={60} value={f.end} disabled={desactive} onChange={(e) => maj(i, { end: e.target.value })} />
              </label>
            </div>
            <p className="sv-reg-creneau__phrase">
              {phraseFenetre(f)}{finLendemain(f) ? ' (la fin est avant le début : le créneau finit le lendemain)' : ''}
            </p>
          </li>
        ))}
      </ol>
      {!desactive ? (
        <button type="button" className="lc-btn" disabled={plein}
                onClick={() => onChange({ t: 'fenetres', v: [...b.v, { days: [], start: '23:00', end: '06:00' }] })}>
          <Plus size={14} aria-hidden="true" /> Ajouter un créneau
        </button>
      ) : null}
    </div>
  )
}

/** Sept lignes de 24 h : chaque créneau peint, une nuit reportée au lendemain. */
export function GrilleSemaine({ fenetres }: { fenetres: Fenetre[] }) {
  const plages = plagesParJour(fenetres)
  return (
    <div className="sv-reg-grille" role="img" aria-label={fenetres.length ? fenetres.map(phraseFenetre).join(' ; ') : 'Aucun créneau'}>
      {JOURS_COURTS.map((j, i) => (
        <div key={j} className="sv-reg-grille__ligne">
          <span className="sv-reg-grille__jour">{j}</span>
          <span className="sv-reg-grille__barre">
            {(plages.get(i + 1) ?? []).map(([a, c], k) => (
              <span key={k} className="sv-reg-grille__plage" style={{ left: `${(a / 1440) * 100}%`, width: `${((c - a) / 1440) * 100}%` }} />
            ))}
          </span>
        </div>
      ))}
      <div className="sv-reg-grille__ligne sv-reg-grille__echelle" aria-hidden="true">
        <span />
        <span className="sv-reg-grille__heures"><span>0 h</span><span>6 h</span><span>12 h</span><span>18 h</span><span>24 h</span></span>
      </div>
    </div>
  )
}

/* ─── threshold : avertissement / critique (+ durée) ─── */
function ChampSeuil({ s, b, onChange, desactive }: PropsChamp & { b: B<'seuil'> }) {
  const maj = (p: Partial<B<'seuil'>>) => onChange({ ...b, ...p })
  const borne = { ...(s.min !== null ? { min: s.min } : {}), ...(s.max !== null ? { max: s.max } : {}) }
  return (
    <div className="sv-reg-seuil">
      <p className="sv-reg-seuil__sens">Alerte quand la valeur passe {phraseSens(s.direction)} :</p>
      <div className="sv-reg-seuil__champs">
        <label className="sv-reg-seuil__champ">
          <span className="lc-label"><span className="sv-reg-point sv-reg-point--warn" aria-hidden="true" /> Avertissement</span>
          {desactive ? <span className="sv-reg-lecture">{b.warn || '—'}{b.warn && s.unit ? ` ${s.unit}` : ''}</span>
            : <NumberInput value={b.warn} onChange={(v) => maj({ warn: v })} unit={s.unit} {...borne} step={s.step ?? 1} ariaLabel={`${s.label} : avertissement`} placeholder="aucun" />}
        </label>
        <label className="sv-reg-seuil__champ">
          <span className="lc-label"><span className="sv-reg-point sv-reg-point--crit" aria-hidden="true" /> Critique</span>
          {desactive ? <span className="sv-reg-lecture">{b.crit || '—'}{b.crit && s.unit ? ` ${s.unit}` : ''}</span>
            : <NumberInput value={b.crit} onChange={(v) => maj({ crit: v })} unit={s.unit} {...borne} step={s.step ?? 1} ariaLabel={`${s.label} : critique`} placeholder="aucun" />}
        </label>
        {s.for_bounds ? (
          <label className="sv-reg-seuil__champ">
            <span className="lc-label">Pendant au moins</span>
            {desactive ? <span className="sv-reg-lecture">{b.for_s || '—'}{b.for_s ? ' s' : ''}</span>
              : <NumberInput value={b.for_s} onChange={(v) => maj({ for_s: v })} unit="s" min={s.for_bounds.min} max={s.for_bounds.max} step={1} steppers ariaLabel={`${s.label} : durée`} placeholder="—" />}
          </label>
        ) : null}
      </div>
      <p className="lc-hint">
        Bornes : {s.min !== null ? s.min.toLocaleString('fr-FR') : '—'} à {s.max !== null ? s.max.toLocaleString('fr-FR') : '—'}{s.unit ? ` ${s.unit}` : ''}
        {s.for_bounds ? ` ; durée de ${s.for_bounds.min} à ${s.for_bounds.max} s` : ''}. Un seuil vide n’alerte pas à ce niveau.
      </p>
    </div>
  )
}

/* ─── enum_map : tableau clés × choix ─── */
function ChampCarte({ s, b, onChange, desactive }: PropsChamp & { b: B<'carte'> }) {
  const opts: SelectOption[] = [{ value: '', label: '—' }, ...(s.options ?? []).map((o) => ({ value: o.value, label: o.label }))]
  return (
    <div className="sv-reg-carte" role="table" aria-label={s.label}>
      {(s.map_keys ?? []).map((k) => (
        <div key={k.value} className="sv-reg-carte__ligne" role="row">
          <span className="sv-reg-carte__cle" role="rowheader">{k.label}</span>
          <span role="cell">
            {desactive ? <span className="sv-reg-lecture">{opts.find((o) => o.value === (b.v[k.value] ?? ''))?.label ?? '—'}</span> : (
              <Select compact value={b.v[k.value] ?? ''} options={opts} ariaLabel={`${s.label} : ${k.label}`}
                      onChange={(v) => {
                        const next = { ...b.v }
                        if (v) next[k.value] = v; else delete next[k.value]
                        onChange({ t: 'carte', v: next })
                      }} />
            )}
          </span>
        </div>
      ))}
    </div>
  )
}

/* ─── json dédié : tableau clé → valeur ─── */
function ChampDico({ s, b, onChange, desactive, ctx }: PropsChamp & { b: B<'dico'> }) {
  const conf = JSON_DEDIES[s.key]!
  const maj = (i: number, l: Partial<LigneDico>) => onChange({ t: 'dico', v: b.v.map((x, j) => j === i ? { ...x, ...l } : x) })
  const zones = ctx.zones
  const choixZone = (v: string, avecExterne: boolean): SelectOption[] => {
    const base = (zones ?? []).map((z) => ({ value: z.value, label: `${z.label} (${z.value})` }))
    if (avecExterne) base.push({ value: 'external', label: 'Location extérieure (external)' })
    if (v && !base.some((o) => o.value === v)) base.unshift({ value: v, label: v })
    return base
  }
  const champCle = (l: LigneDico, i: number) => {
    if (desactive) return <span className="sv-reg-lecture sv-mono">{l.cle || '—'}</span>
    if (conf.genreCle === 'zone' && zones) {
      return <Select compact value={l.cle} options={choixZone(l.cle, false)} onChange={(v) => maj(i, { cle: v })} ariaLabel={`${conf.cle} ${i + 1}`} placeholder="Choisir…" />
    }
    return <TextInput value={l.cle} onChange={(v) => maj(i, { cle: v })} ariaLabel={`${conf.cle} ${i + 1}`} maxLength={120} />
  }
  const champValeur = (l: LigneDico, i: number) => {
    if (desactive) return <span className="sv-reg-lecture sv-mono">{l.valeur || '—'}{conf.unite && l.valeur ? ` ${conf.unite}` : ''}</span>
    const aria = `${conf.valeur} de ${l.cle || `la ligne ${i + 1}`}`
    if (conf.genreValeur === 'choix' && conf.options) {
      const o = conf.options.some((x) => x.value === l.valeur) || !l.valeur ? conf.options : [{ value: l.valeur, label: l.valeur }, ...conf.options]
      return <Select compact value={l.valeur} options={o} onChange={(v) => maj(i, { valeur: v })} ariaLabel={aria} placeholder="Choisir…" />
    }
    if ((conf.genreValeur === 'zone' || conf.genreValeur === 'zone_ou_externe') && zones) {
      return <Select compact value={l.valeur} options={choixZone(l.valeur, conf.genreValeur === 'zone_ou_externe')} onChange={(v) => maj(i, { valeur: v })} ariaLabel={aria} placeholder="Choisir…" />
    }
    if (conf.genreValeur === 'entier') {
      return <NumberInput value={l.valeur} onChange={(v) => maj(i, { valeur: v })} unit={conf.unite} min={1} step={1} ariaLabel={aria} />
    }
    if (conf.genreValeur === 'temoin') {
      const vote = l.valeur === 'vote'
      return (
        <span className="sv-reg-temoin">
          <Checkbox checked={vote} label="Vote des appareils" onChange={(on) => maj(i, { valeur: on ? 'vote' : '' })} />
          {!vote ? <TextInput value={l.valeur} onChange={(v) => maj(i, { valeur: v })} ariaLabel={`${aria} (uid de l’appareil racine)`} placeholder="uid de l’appareil racine" maxLength={120} /> : null}
        </span>
      )
    }
    return <TextInput value={l.valeur} onChange={(v) => maj(i, { valeur: v })} ariaLabel={aria} maxLength={120} />
  }
  return (
    <div className="sv-reg-dico">
      <div className="sv-reg-dico__entete" aria-hidden="true"><span>{conf.cle}</span><span>{conf.valeur}</span><span /></div>
      {b.v.length === 0 ? <p className="lc-hint">Aucune ligne.</p> : null}
      {b.v.map((l, i) => (
        <div key={i} className="sv-reg-dico__ligne">
          <span className="sv-reg-dico__cell" data-titre={conf.cle}>{champCle(l, i)}</span>
          <span className="sv-reg-dico__cell" data-titre={conf.valeur}>{champValeur(l, i)}</span>
          {!desactive ? (
            <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" aria-label={`Retirer ${l.cle || `la ligne ${i + 1}`}`}
                    onClick={() => onChange({ t: 'dico', v: b.v.filter((_, j) => j !== i) })}>
              <Trash2 size={15} aria-hidden="true" />
            </button>
          ) : <span />}
        </div>
      ))}
      {!desactive ? (
        <button type="button" className="lc-btn" onClick={() => onChange({ t: 'dico', v: [...b.v, { cle: '', valeur: '' }] })}>
          <Plus size={14} aria-hidden="true" /> {conf.ajout}
        </button>
      ) : null}
    </div>
  )
}

/* ─── json inconnu : texte JSON validé ─── */
function ChampJson({ s, b, onChange, desactive }: PropsChamp & { b: B<'json'> }) {
  const id = useId()
  let valide = true
  if (b.v.trim()) { try { JSON.parse(b.v) } catch { valide = false } }
  return (
    <span className="sv-reg-json">
      <textarea id={id} className={`lc-input sv-reg-json__texte${valide ? '' : ' lc-input--error'}`} value={b.v} disabled={desactive}
                spellCheck={false} aria-label={`${s.label} (texte JSON)`} aria-invalid={!valide}
                rows={Math.min(14, Math.max(4, b.v.split('\n').length))} onChange={(e) => onChange({ t: 'json', v: e.target.value })} />
      <span className={`lc-hint${valide ? '' : ' sv-reg-erreur'}`}>{valide ? 'Texte JSON, vérifié avant l’envoi.' : 'Texte JSON invalide : rien ne partira tant qu’il n’est pas corrigé.'}</span>
    </span>
  )
}
